import { useEffect, useState } from 'react';

import { RELOAD_CAL_MODAL, SessionCancelChoice } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { isMomentsSession as hasMomentsSessionIdentity } from '@/types';

import CancelRecurringSession from './cancelRecurringSession';
import CancelSingleSession from './cancelSingleSession';
import ProviderAwareSessionAction from './providerAwareSessionAction';
import ScheduleSession from './scheduleSession';

import type { Session } from '@/types';
import type { FC } from 'react';

import CancelPrompt from '@/components/Cal/cancelPrompt';
import ReschedulePrompt from '@/components/Cal/reschedulePrompt';
import { MemberCancelSession } from '@/components/MentoCal/MemberCancelSession';
import { useGetSessionQuery } from '@/store/api/sessionApi';

interface CancelSessionProps {
  allowMemberMomentsCancellation?: boolean;
  close?: () => void;
  onCancelled?: () => void;
  onRescheduleInstead?: () => void;
  session: Session;
}

const CancelSession: FC<CancelSessionProps> = ({
  session: currentSession,
  allowMemberMomentsCancellation = false,
  close,
  onCancelled,
  onRescheduleInstead,
}) => {
  const [loadedSession, setLoadedSession] = useState<Session>();
  let session =
    loadedSession?.id === currentSession.id ? loadedSession : undefined;
  if (currentSession.sourceAppointmentId) session = currentSession;
  const [sessionCancelChoice, setSessionCancelChoice] =
    useState<SessionCancelChoice>(SessionCancelChoice.PENDING);
  const { user } = useCurrentUser();

  const { data } = useGetSessionQuery(currentSession.id, {
    skip: Boolean(currentSession.sourceAppointmentId) || !currentSession.id,
  });

  useEffect(() => {
    if (data) {
      setLoadedSession({
        ...data,
        startedAt: data.startedAt,
      });
      setSessionCancelChoice(
        data.recurringId
          ? SessionCancelChoice.PENDING
          : SessionCancelChoice.SINGLE,
      );
    }
  }, [data]);

  const onContinue = (choice: SessionCancelChoice) =>
    setSessionCancelChoice(choice);
  const onRecurringClose = () => {
    setSessionCancelChoice(SessionCancelChoice.RESCHEDULE_PROMPT);
  };
  const onRecurringContinue = () =>
    setSessionCancelChoice(SessionCancelChoice.RESCHEDULE);

  const onClose = () => {
    if (!close) {
      setTimeout(() => window.location.reload(), RELOAD_CAL_MODAL);
    } else {
      close();
    }
  };

  if (!session) {
    return null;
  }

  const isMomentsSession =
    Boolean(session.sourceAppointmentId) || hasMomentsSessionIdentity(session);
  if (isMomentsSession) {
    if (!allowMemberMomentsCancellation) {
      return (
        <ProviderAwareSessionAction
          action="cancel"
          close={onClose}
          session={session}
        />
      );
    }

    return (
      <MemberCancelSession
        key={session.sourceAppointmentId ?? session.id}
        close={onClose}
        session={session}
        userId={user.id}
        onCancelled={onCancelled}
        onRescheduleInstead={onRescheduleInstead}
      />
    );
  }

  switch (sessionCancelChoice) {
    case SessionCancelChoice.PENDING:
      return (
        <CancelPrompt
          session={session}
          onClose={onClose}
          onContinue={onContinue}
        />
      );
    case SessionCancelChoice.RECURRING:
      return (
        <CancelRecurringSession close={onRecurringClose} session={session} />
      );
    case SessionCancelChoice.SINGLE:
      return <CancelSingleSession close={onClose} session={session} />;
    case SessionCancelChoice.RESCHEDULE_PROMPT:
      return (
        <ReschedulePrompt
          session={session}
          onClose={onClose}
          onContinue={onRecurringContinue}
        />
      );
    case SessionCancelChoice.RESCHEDULE:
      return (
        <ScheduleSession
          client={user}
          close={onClose}
          coach={session.coach}
          eventTypeSlug="bi-weekly-coaching-session"
        />
      );
    default:
      return null;
  }
};

export default CancelSession;
